import React, { useCallback, useEffect, useState } from 'react';
import { LoadingSpinner } from '../../common';
import { EdiService, formatCents, formatEdiDate } from '../../../services/billing/edi.service';
import { WORK_KINDS, WORK_STATES } from '../../../dtos/billing/edi.dto';
import type { EdiWorkItem, EdiWorklistPage, EdiWorklistQuery, WorkKind, WorkState } from '../../../dtos/billing/edi.dto';
import ExceptionDetail from './ExceptionDetail';
import { Cell, ErrorNote, SimulatedBadge, WORK_KIND_LABEL, WORK_STATE_LABEL, errorText } from './ediUi';

interface ExceptionsViewProps {
  /** Open this item first (from the dashboard). */
  initialItemId?: string | null;
}

type Filters = Required<Pick<EdiWorklistQuery, 'kind' | 'state' | 'payerId' | 'patientId'>>;
const EMPTY: Filters = { kind: '', state: '', payerId: '', patientId: '' };
const selectClass = 'w-full rounded-md border border-slate-300 px-2 py-1.5 text-base sm:text-sm';

/** The agency's exception worklist (DEN-01, DEN-06): filters, list, item detail. */
const ExceptionsView: React.FC<ExceptionsViewProps> = ({ initialItemId = null }) => {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [items, setItems] = useState<EdiWorkItem[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(initialItemId);
  // Payer and patient choices: the server's `filters` lists, plus any seen on loaded rows (the API filters by id)
  const [payers, setPayers] = useState<Map<string, string>>(new Map());
  const [patients, setPatients] = useState<Map<string, string>>(new Map());

  const remember = (page: EdiWorklistPage) => {
    setPayers((prev) => {
      const next = new Map(prev);
      page.filters.payers.forEach((o) => next.set(o.id, o.name));
      page.items.forEach((i) => { if (i.payerId && !next.has(i.payerId)) next.set(i.payerId, i.payerName || i.payerId); });
      return next;
    });
    setPatients((prev) => {
      const next = new Map(prev);
      page.filters.patients.forEach((o) => next.set(o.id, o.name));
      page.items.forEach((i) => { if (i.patientId && !next.has(i.patientId)) next.set(i.patientId, i.patientName || 'Patient'); });
      return next;
    });
  };

  const load = useCallback(async (more?: string) => {
    setError(null);
    try {
      const page = await EdiService.listWorklist({ ...filters, cursor: more });
      remember(page);
      setItems((prev) => (more && prev ? [...prev, ...page.items] : page.items));
      setCursor(page.cursor);
    } catch (err) {
      setError(errorText(err, 'Failed to load exceptions'));
    }
  }, [filters]);

  useEffect(() => { if (!openId) void load(); }, [load, openId]);

  if (openId) {
    return (
      <ExceptionDetail
        workItemId={openId}
        onBack={() => setOpenId(null)}
        onChanged={(updated) => setItems((prev) => prev?.map((i) => (i.workItemId === updated.workItemId ? updated : i)) ?? prev)}
      />
    );
  }

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-3 p-3 sm:p-4" aria-label="Exceptions">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <label className="text-sm text-slate-700">Kind
          <select aria-label="Kind" value={filters.kind} onChange={(e) => set('kind', e.target.value as WorkKind | '')} className={selectClass}>
            <option value="">All kinds</option>
            {WORK_KINDS.map((k) => <option key={k} value={k}>{WORK_KIND_LABEL[k] || k}</option>)}
          </select>
        </label>
        <label className="text-sm text-slate-700">State
          <select aria-label="State" value={filters.state} onChange={(e) => set('state', e.target.value as WorkState | '')} className={selectClass}>
            <option value="">All open</option>
            {WORK_STATES.map((s) => <option key={s} value={s}>{WORK_STATE_LABEL[s] || s}</option>)}
          </select>
        </label>
        <label className="text-sm text-slate-700">Payer
          <select aria-label="Payer" value={filters.payerId} onChange={(e) => set('payerId', e.target.value)} className={selectClass}>
            <option value="">All payers</option>
            {Array.from(payers.entries()).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <label className="text-sm text-slate-700">Patient
          <select aria-label="Patient" value={filters.patientId} onChange={(e) => set('patientId', e.target.value)} className={selectClass}>
            <option value="">All patients</option>
            {Array.from(patients.entries()).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
      </div>

      <ErrorNote message={error} />
      {!items && !error && <LoadingSpinner label="Loading exceptions" />}
      {items && items.length === 0 && <p className="text-sm text-slate-500">No exceptions match these filters.</p>}
      {items && items.length > 0 && (
        <div className="text-sm">
          <div className="hidden grid-cols-6 gap-2 border-b border-slate-100 pb-1 text-xs uppercase text-slate-500 sm:grid">
            <span>Kind</span><span className="col-span-2">Reason</span><span>Patient · payer</span><span className="text-right">Amount</span><span>Deadline · state</span>
          </div>
          {items.map((i) => (
            <button
              key={i.workItemId}
              type="button"
              onClick={() => setOpenId(i.workItemId)}
              className="grid w-full grid-cols-1 gap-1 border-b border-slate-100 py-2 text-left hover:bg-slate-50 sm:grid-cols-6 sm:gap-2"
              data-testid={`work-${i.workItemId}`}
            >
              <Cell label="Kind" className="font-medium">{WORK_KIND_LABEL[i.kind] || i.kind}{i.simulated && <SimulatedBadge className="ml-1" />}</Cell>
              <Cell label="Reason" className="sm:col-span-2">{i.reason.text || i.reason.code}</Cell>
              <Cell label="Patient">{[i.patientName, i.payerName].filter(Boolean).join(' · ') || '—'}</Cell>
              <Cell label="Amount" className="sm:text-right">{i.amountCents !== null ? formatCents(i.amountCents) : '—'}</Cell>
              <Cell label="Deadline">{i.deadline ? formatEdiDate(i.deadline) : '—'} · {WORK_STATE_LABEL[i.state] || i.state}</Cell>
            </button>
          ))}
          {cursor && (
            <button type="button" onClick={() => load(cursor)} className="mt-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700">Load more</button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExceptionsView;
